import servicesUtils from '../utils/service'

/**
 * 被素材下架卡住的題目 — 見 mangachat-studio-api `docs/plans/MediaLibrary.spec.md`
 * §2.4、[ADR 0009](../../../mangachat-studio-api/docs/adr/0009-delisting-blocks-quests-instead-of-deleting-them.md)。
 *
 * ⚠ **為什麼需要這兩支**：被停用的題目（`status = '-2'`）**不會**出現在關卡列表裡
 * （後端的 `getUnitList` 濾 `status = '1'`），所以沒有它們的話那些題目就是一批
 * 看不見的孤兒——而另一條恢復路徑（原素材重新上架）不在老師手上，素材是別人的。
 *
 * ⚠ 與 `services/media.js` 同一個慣例：每一支都傳 `{ handleError: true }`，
 * 否則 `checkStatus` 會對任何非 2xx 直接 `router.push` 換頁，呼叫端的 catch
 * 跑的時候畫面已經換掉了。
 */

/** 拆掉 `BaseAction` 的信封（`{ data, err }`）。與 media.js 的 unwrap 同一個理由。 */
function unwrap(res) {
    return res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res
}

const HANDLE_ERROR = { handleError: true }

export const blockedQuestServices = {
    /**
     * 有幾道題目被卡住、散在幾本範本。**給橫幅用。**
     *
     * ⚠ **這一支每一頁都會打**（橫幅掛在版面層），所以後端刻意只回兩個數字：
     * 不 join 素材、不解析 `resources`。**不要在這裡改成拿清單**。
     *
     * ⚠ `blocked_homework_count` 只用在**橫幅文案**（「散在 N 本範本」）。
     * 它**不能**用來直連某一本範本——摘要刻意不回 id，只有一本的時候也不知道是哪一本。
     *
     * @returns {Promise<{blocked_quest_count:number, blocked_homework_count:number}>}
     */
    async getSummary() {
        try {
            const url = `${servicesUtils.resourceUrl('blockedQuestSummary')}`
            return unwrap(await servicesUtils.get(url, HANDLE_ERROR))
        } catch (err) {
            console.log(`failed to getBlockedQuestSummary err: ${err}`)
            throw err
        }
    },

    /**
     * 完整清單：每一道被卡住的題目，以及**卡住它的素材**（名稱、縮圖、是誰的）。
     *
     * ⚠ **沒有分頁**（後端也沒做）：這個清單的正常長度是 0，不是 0 的時候老師會想
     * 一次看完處理掉，而分頁會讓他以為只有那幾道。
     *
     * ⚠⚠ **每一份素材的 `is_mine` 一定要用它分岔 UI**：
     * 是自己的素材 → 他可以**直接重新上架**（一次解決全部被卡住的題目）；
     * 是別人的 → 只能換掉素材或刪掉題目，因為決定權在對方（ADR 0009）。
     * 不分岔的話老師會以為自己沒有選擇。
     *
     * ⚠ 一道題目可能被**多份**素材卡住（翻卡牌用了兩張圖、兩張都被下架）。
     * 只解開其中一份時那題**還不會**開放。
     *
     * @returns {Promise<{total:number, items:Array<{
     *   quest_id:number, quest_title:string, diary_homework_id:number,
     *   homework_name:string, unit:number|null,
     *   blocked_media:Array<{media_id:number, kind:string, purpose:string, title:string,
     *     url:string|null, width:number|null, height:number|null,
     *     attributes:Object|null, owner_type:string, is_mine:boolean, status:string}>
     * }>}>}
     */
    async getList() {
        try {
            const url = `${servicesUtils.resourceUrl('blockedQuestList')}`
            return unwrap(await servicesUtils.get(url, HANDLE_ERROR))
        } catch (err) {
            console.log(`failed to getBlockedQuestList err: ${err}`)
            throw err
        }
    },
}
